import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { ArrowRight, Loader2 } from 'lucide-react';
import { ZoomImage } from './ZoomImage';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, []);

  const handleExplore = (categoryName) => {
    navigate(`/search?category=${encodeURIComponent(categoryName)}`);
  };

  if (loading) {
    return (
      <section className="py-24 bg-[#F5F3F0]">
        <div className="container mx-auto px-4 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#C4B5A0] mx-auto mb-4" />
            <p className="text-[#8B7D6B] font-montserrat">Loading categories...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-[#F5F3F0]" data-testid="categories-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#8B7D6B] font-montserrat text-sm tracking-[0.2em] uppercase mb-4">
            Explore by Category
          </p>
          <h2 className="text-4xl md:text-5xl font-seasons font-light text-[#2C2C2C] mb-6">
            Shop By Collection
          </h2>
          <p className="text-[#8B7D6B] font-montserrat max-w-2xl mx-auto">
            From everyday elegance to special occasion splendor
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {categories.map((category) => (
            <Card
              key={category.id}
              data-testid={`category-card-${category.id}`}
              onClick={() => handleExplore(category.name)}
              className="group relative overflow-hidden border-none shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              {/* Category Image with Zoom */}
              <div className="relative h-80 overflow-hidden">
                <ZoomImage
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300 pointer-events-none"></div>
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform transition-transform duration-300 group-hover:translate-y-[-8px] pointer-events-none">
                  <p className="font-montserrat text-xs tracking-wider uppercase mb-2 text-white/80">
                    {category.count}+ Designs
                  </p>
                  <h3 className="font-seasons text-2xl mb-2 text-white">
                    {category.name}
                  </h3>
                  <p className="font-montserrat text-sm text-white/90 mb-4">
                    {category.description}
                  </p>
                  
                  {/* Explore Button */}
                  <button
                    type="button"
                    data-testid={`category-explore-${category.id}`}
                    onClick={(e) => { e.stopPropagation(); handleExplore(category.name); }}
                    className="pointer-events-auto inline-flex items-center gap-2 text-white font-montserrat text-sm tracking-wider uppercase opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <span>Explore</span>
                    <ArrowRight className="h-4 w-4 transform group-hover:translate-x-2 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
